import { useCallback, useEffect, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import * as legalApi from '../../api/legal';
import type { LegalDocumentSummary } from '../../api/legal';
import { LEGAL_KIND_LABEL, LEGAL_KIND_PATH } from '../../api/legal';

/**
 * The join forms' Terms / Privacy checkbox (LEGAL-TERMS). Loads the
 * current versions; until a platform admin publishes one there is nothing
 * to accept, the box isn't shown and `ready` is true. Unticked by default:
 * the click is the consent the server records.
 */
export function useLegalConsent() {
  const [docs, setDocs] = useState<LegalDocumentSummary[] | null>(null);
  const [accepted, setAccepted] = useState(false);

  const reload = useCallback(async () => {
    setAccepted(false);
    try {
      setDocs((await legalApi.getCurrentLegal()).current);
    } catch {
      // The server still enforces; a failed load only hides the box.
      setDocs([]);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const required = (docs?.length ?? 0) > 0;
  return {
    docs: docs ?? [],
    loaded: docs !== null,
    required,
    accepted,
    setAccepted,
    ready: docs !== null && (!required || accepted),
    /** What the join request sends: empty unless ticked. */
    documentIds: accepted ? (docs ?? []).map((d) => d.id) : [],
    reload,
  };
}

/** "Terms of Use and Privacy Policy" as links that open in a new tab. */
export function LegalLinks({ docs }: { docs: LegalDocumentSummary[] }) {
  return (
    <>
      {docs.map((d, i) => (
        <span key={d.id}>
          {i > 0 && (i === docs.length - 1 ? ' and ' : ', ')}
          <MuiLink
            href={LEGAL_KIND_PATH[d.kind]}
            target="_blank"
            rel="noopener"
            onClick={(e) => e.stopPropagation()}
          >
            {LEGAL_KIND_LABEL[d.kind]}
          </MuiLink>
        </span>
      ))}
    </>
  );
}

export function LegalConsentCheckbox({
  consent,
  disabled,
}: {
  consent: ReturnType<typeof useLegalConsent>;
  disabled?: boolean;
}) {
  if (!consent.required) return null;
  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={consent.accepted}
          onChange={(e) => consent.setAccepted(e.target.checked)}
          disabled={disabled}
        />
      }
      label={
        <Typography variant="body2">
          I have read and agree to the <LegalLinks docs={consent.docs} />.
        </Typography>
      }
    />
  );
}

/** A 428 from a join request: the documents changed (or weren't ticked). */
export function isLegalConflict(err: unknown): boolean {
  return (
    (err as { response?: { status?: number } })?.response?.status === 428
  );
}
